import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiService from '../services/api';

/**
 * Trends Discovery Page
 * Shows trending hashtags, sounds, effects, and topics
 */

const TREND_TABS = [
  { id: 'all', label: 'All', icon: '🔥' },
  { id: 'hashtag', label: 'Hashtags', icon: '#️⃣' },
  { id: 'sound', label: 'Sounds', icon: '🎵' },
  { id: 'effect', label: 'Effects', icon: '✨' },
  { id: 'topic', label: 'Topics', icon: '💬' },
];

const TIME_PERIODS = [
  { value: 1, label: '24h' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
];

export default function TrendsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [timePeriod, setTimePeriod] = useState(7);
  const [trends, setTrends] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch trending data
  const fetchTrends = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        days: timePeriod,
        limit: 50,
      });
      
      if (activeTab !== 'all') {
        params.append('trend_type', activeTab);
      }

      const response = await apiService.get(`/api/trends/metrics/trending?${params}`);
      setTrends(response.trends || []);
    } catch (err) {
      setError(err.message || 'Failed to load trends');
      console.error('Failed to fetch trends:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, timePeriod]);

  // Fetch predictions
  const fetchPredictions = useCallback(async () => {
    try {
      const response = await apiService.get('/api/trends/predictions/upcoming?limit=10');
      setPredictions(response.predictions || []);
    } catch (err) {
      console.error('Failed to fetch predictions:', err);
    }
  }, []);

  useEffect(() => {
    fetchTrends();
    fetchPredictions();
  }, [fetchTrends, fetchPredictions]);

  // Format large numbers
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  // Get trend type icon
  const getTrendIcon = (type) => {
    switch (type) {
      case 'hashtag': return '#️⃣';
      case 'sound': return '🎵';
      case 'effect': return '✨';
      case 'topic': return '💬';
      default: return '🔥';
    }
  };

  // Get velocity indicator
  const getVelocityIndicator = (score) => {
    if (score >= 80) return { color: 'text-green-500', icon: '🚀', label: 'Explosive' };
    if (score >= 60) return { color: 'text-yellow-500', icon: '📈', label: 'Rising Fast' };
    if (score >= 40) return { color: 'text-blue-500', icon: '↗️', label: 'Growing' };
    return { color: 'text-gray-500', icon: '→', label: 'Stable' };
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              🔥 Trending
            </h1>
            
            {/* Time period selector */}
            <div className="flex bg-gray-800 rounded-lg p-1">
              {TIME_PERIODS.map((period) => (
                <button
                  key={period.value}
                  onClick={() => setTimePeriod(period.value)}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    timePeriod === period.value
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {period.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {TREND_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main trends list */}
          <div className="lg:col-span-2">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent" />
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-red-400 mb-4">{error}</p>
                <button
                  onClick={fetchTrends}
                  className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700"
                >
                  Try Again
                </button>
              </div>
            ) : trends.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-400">No trends found for this period</p>
              </div>
            ) : (
              <div className="space-y-3">
                {trends.map((trend, index) => {
                  const velocity = getVelocityIndicator(trend.velocity_score);
                  return (
                    <motion.div
                      key={`${trend.trend_type}-${trend.trend_value}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-gray-800 rounded-xl p-4 hover:bg-gray-750 cursor-pointer transition-colors"
                      onClick={() => navigate(`/explore?q=${encodeURIComponent(trend.trend_value)}`)}
                    >
                      <div className="flex items-center gap-4">
                        {/* Rank */}
                        <div className="w-8 h-8 flex items-center justify-center">
                          {index < 3 ? (
                            <span className="text-2xl">
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                            </span>
                          ) : (
                            <span className="text-gray-500 font-bold">{trend.rank}</span>
                          )}
                        </div>

                        {/* Trend info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span>{getTrendIcon(trend.trend_type)}</span>
                            <span className="font-semibold truncate">
                              {trend.trend_type === 'hashtag' ? '#' : ''}
                              {trend.trend_value}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                            <span>📹 {formatNumber(trend.total_posts)} posts</span>
                            <span>❤️ {formatNumber(trend.total_engagement)} engagement</span>
                            <span>👤 {formatNumber(trend.unique_creators)} creators</span>
                          </div>
                        </div>

                        {/* Velocity indicator */}
                        <div className={`text-right ${velocity.color}`}>
                          <div className="flex items-center gap-1">
                            <span>{velocity.icon}</span>
                            <span className="font-bold">{trend.velocity_score}</span>
                          </div>
                          <span className="text-xs">{velocity.label}</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar - Predictions */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-xl p-4 sticky top-32">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
                🔮 Predicted to Trend
              </h2>
              
              {predictions.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  No predictions available yet
                </p>
              ) : (
                <div className="space-y-3">
                  {predictions.map((pred, index) => (
                    <motion.div
                      key={`${pred.trend_type}-${pred.trend_value}`}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-700 cursor-pointer"
                      onClick={() => navigate(`/explore?q=${encodeURIComponent(pred.trend_value)}`)}
                    >
                      <span>{getTrendIcon(pred.trend_type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-sm">
                          {pred.trend_type === 'hashtag' ? '#' : ''}
                          {pred.trend_value}
                        </p>
                        <p className="text-xs text-gray-500">
                          {Math.round(pred.predicted_score * 100)}% confidence
                        </p>
                      </div>
                      <div className="text-green-500 text-xs">↗️ Soon</div>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Trend tips */}
              <div className="mt-6 pt-4 border-t border-gray-700">
                <h3 className="font-semibold text-sm mb-2 text-gray-400">
                  💡 Tips for Creators
                </h3>
                <ul className="text-xs text-gray-500 space-y-2">
                  <li>• Jump on rising trends early for maximum reach</li>
                  <li>• Combine multiple trending elements</li>
                  <li>• Put your unique spin on popular formats</li>
                  <li>• Check back daily for new opportunities</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
